/**
 * Calendar-date helpers shared by the client and the server. Without a time zone they use the local one (the
 * browser's, or the server's TZ); the server passes the user's zone (settings.timeZone) so the hosted edition, which
 * runs in UTC, counts days the way the user does.
 */
import { dayKey, daysBetweenKeys } from './zone.js';

const DATE_ONLY = /^(\d{4})-(\d{2})-(\d{2})$/;

/** Whole calendar days from a 'YYYY-MM-DD' (or ISO) date until now. null if no date. */
export function daysSince(value: string | null | undefined, now: Date = new Date(), timeZone?: string): number | null {
  if (!value) return null;
  if (timeZone) {
    const from = calendarDay(value, timeZone);
    return from ? daysBetweenKeys(from, dayKey(now, timeZone)) : null;
  }
  const d = parseDate(value);
  return d ? daysBetween(d, now) : null;
}

/** A date-only value is already a calendar day; a timestamp falls on a day in `timeZone`. */
function calendarDay(value: string, timeZone: string): string | null {
  if (DATE_ONLY.test(value)) return value;
  const time = Date.parse(value);
  return Number.isNaN(time) ? null : dayKey(new Date(time), timeZone);
}

/** Parse 'YYYY-MM-DD' as a local date (not UTC) and ISO timestamps normally. */
export function parseDate(value: string): Date | null {
  const m = DATE_ONLY.exec(value);
  const d = m ? new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3])) : new Date(value);
  return Number.isNaN(d.getTime()) ? null : d;
}

/** Whole calendar days from a to b (DST-safe: compares local midnights). */
export function daysBetween(a: Date, b: Date): number {
  const a0 = new Date(a.getFullYear(), a.getMonth(), a.getDate()).getTime();
  const b0 = new Date(b.getFullYear(), b.getMonth(), b.getDate()).getTime();
  return Math.round((b0 - a0) / 86400000);
}
